/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "../../examples/Tables/DataTable";
import useApi from "../../components/Utils/api";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils";
import PermissionControl from "../../components/Utils/PermissionControl";

function SystemConfig() {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [configValue, setConfigValue] = useState("");
  const [loading, setLoading] = useState(false);
  const api = useApi();

  const columns = [
    { Header: "Config Name", accessor: "name", width: "20%", align: "left" },
    { Header: "Current Value", accessor: "value", width: "15%", align: "left" },
    { Header: "Description", accessor: "description", width: "35%", align: "left" },
    { Header: "Last Updated", accessor: row => formatDate(row.updatedAt), width: "15%", align: "left" },
    { Header: "Last Updated By", accessor: "updatedBy", width: "15%", align: "left" },
    { Header: "Action", accessor: "action", width: "15%", align: "left" },
  ];

  const getConfigs = () => {
    setLoading(true);
    api.get("/system-config/")
      .then((response) => {
        if (response.data && Array.isArray(response.data)) {
          const modifiedData = response.data.map(item => {
            const action = (
            <PermissionControl permission={{ restrict: true, module: "SystemConfiguration", subModule: "Update" }} >
              <MDButton
                variant="contained"
                size="small"
                color="info"
                onClick={() => handleEditClick(item)}
              >
                Edit
              </MDButton>
            </PermissionControl>
            );
            return { ...item, action };
          });
          setConfigs(modifiedData);
        } else {
          toast.error("Failed to load configurations", { duration: 3000 });
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to view configurations.", { id: "PermissionError" });
          return;
        }
        toast.error("Failed to load configurations", { duration: 3000 });
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEditClick = (config) => {
    setSelectedConfig(config);
    setConfigValue(config.value);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedConfig(null);
    setConfigValue("");
  };

  const handleUpdateConfig = () => {
    if (!selectedConfig || configValue.trim() === "") {
      toast.error("Please enter a valid value", { duration: 3000 });
      return;
    }

    setLoading(true);
    api.put(`/system-config/${selectedConfig.name}`, {
      value: configValue,
    })
      .then((response) => {
        if (response.data) {
          toast.success(`Configuration "${selectedConfig.name}" updated successfully!`, {
            icon: '✅',
            duration: 3000,
          });
          handleCloseDialog();
          getConfigs();
        }
      })
      .catch((error) => {
        toast.error("Failed to update configuration", { duration: 3000 });
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getConfigs();
  }, []);

  return (
    <DashboardLayout>
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" gutterBottom>
                  System Configuration
                </MDTypography>
                <DataTable
                  table={{ columns, rows: configs }}
                  showTotalEntries={true}
                  isSorted={true}
                  noEndBorder
                  entriesPerPage={true}
                  canSearch={true}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Configuration: {selectedConfig?.name}
        </DialogTitle>
        <DialogContent>
          <MDBox mt={2}>
            <MDTypography variant="caption" color="textSecondary" display="block" mb={1}>
              Description: {selectedConfig?.description}
            </MDTypography>
            <TextField
              fullWidth
              label="Configuration Value"
              value={configValue}
              onChange={(e) => setConfigValue(e.target.value)}
              variant="outlined"
              size="small"
              placeholder="Enter new value"
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseDialog}>
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="contained"
            onClick={handleUpdateConfig}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default SystemConfig;
